<template>
  <div :class="$style.background">
    <div
      :class="hasPackageRecommendation ? $style.headerBackground : $style.border"
    >
      <div
        v-if="hasPackageRecommendation"
        :class="[$style.headerPlanBlock, $style.headerRecommend]"
      >
        <CustomIcon
          v-if="!hasUsageRecommendation"
          :class="$style.highlightTagIcon"
          :scale="1.5"
        >
          <HighlightTagIcon />
        </CustomIcon>
        <span data-test-id="Subscription__recommendationText">
          {{ hasUsageRecommendation ? 'RECOMMENDED BASED ON USAGE' : 'RECOMMENDED' }}
        </span>
      </div>
      <div
        v-else
        :class="[$style.headerPlanBlock, $style.headerEmpty]"
      />
      <div :class="$style.body">
        <HeadingLarge>
          {{ item.displayName }}
        </HeadingLarge>
        <p :class="$style.description">
          {{ details.description }}
        </p>
        <!-- Price -->
        <div :class="$style.price">
          <transition
            mode="out-in"
            :name="$style.fade"
          >
            <span
              :key="`Price_${billingType}`"
              :class="$style.priceAmount"
              data-test-id="Subscription__price"
            >
              {{ price(item.productCode) }}
              <span :class="[$style.priceDollar, $style.priceText]">
                $
              </span>
            </span>
          </transition>
          <p
            v-if="!isAnnual"
            :class="[$style.priceText, $style.priceDescription]"
          >
            monthly price per user
          </p>
          <p
            v-else
            :class="[$style.priceText, $style.priceDescription]"
          >
            monthly price per user, billed annually
          </p>
        </div>
        <!-- Action -->
        <transition
          mode="out-in"
          :name="$style.fade"
        >
          <div>
            <Button
              v-if="hasPackageRecommendation"
              data-test-id="Subscription__packageRecommendationBuyNowButton"
              :text="button.text"
              @click="button.action($options.modalName, item)"
            />
            <Button
              v-else
              :data-test-id="button.dataTestId"
              :outline="true"
              :text="button.text"
              type="secondary"
              @click="button.action($options.modalName, item)"
            />
          </div>
        </transition>
        <ButtonLink
          v-if="isPricingPage"
          :class="$style.detailsButton"
          data-test-id="Subscription_detailsButton"
          text="details"
          textCase="lowercase"
          @click="handleDetailsClick(item)"
        />
      </div>
    </div>
    <!-- Features -->
    <div :class="$style.features">
      <HeadingExtraSmall>
        {{ details.specification.description }}
      </HeadingExtraSmall>
      <div
        v-for="group in details.specification.groups"
        :key="group.name"
      >
        <p :class="$style.groupName">
          {{ group.name }}
        </p>
        <div
          v-for="feature in featuresWithAssociatedPlan(group.features)"
          :key="feature.attribute"
          :class="$style.featureWrapper"
          data-test-id="Subscription__checkmarkAttribute"
        >
          <Icon
            :class="$style.featureIcon"
            icon="checkmarkCircle"
            :scale="1"
          />
          <div
            v-if="feature.attribute === $options.Attributes.cloudDirectoryAndSupport"
            :class="$style.attribute"
            data-test-id="Subscription__cloudDirectoryAndSupport"
          >
            <Tooltip
              :text="getCloudDirectoryDescription(feature.attribute)"
            >
              <span :class="$style.attributeWithDescription">
                {{ getCloudDirectoryName(feature.attribute) }}
              </span>
            </Tooltip>
            &
            <Tooltip
              :text="getStandardSupportDescription(feature.attribute)"
            >
              <span :class="$style.attributeWithDescription">
                {{ getStandardSupportName(feature.attribute) }}
              </span>
            </Tooltip>
          </div>
          <div v-else>
            <Tooltip
              :class="$style.attribute"
              :text="getAttributeDescription(feature.attribute)"
            >
              <span :class="$style.attributeWithDescription">
                {{ getAttributeDisplayName(feature.attribute) }}
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  Button,
  ButtonLink,
  CustomIcon,
  HeadingExtraSmall,
  HeadingLarge,
  Icon,
  Tooltip,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
} from 'client/util/Constants';
import {
  eventLocations,
  eventProperties,
  paymentModalNames,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import HighlightTagIcon from 'public/img/Payments/highlight-tag.svg?inline';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import PaymentHelper from 'client/Payments/PaymentHelper';
import PaymentStepMixin from 'client/Payments/mixins/PaymentStepMixin';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import SubscriptionFeaturesMixin from 'client/Payments/pricingMatrix/SubscriptionFeaturesMixin';
import Util from 'client/util/Util';

const checkoutModel = 'CheckoutModel';
const planModule = 'PlanModule';
const subscriptionCollection = 'SubscriptionCollection';

const {
  getProductCodeByType,
  Attributes,
  ProductCodeToSubscriptionType,
  SubscriptionDetails,
  SubscriptionTypes,
} = SubscriptionConstants;
const { fixDecimals } = Util;

const {
  description,
  location: locationEventProp,
  text,
  section,
} = eventProperties;
const {
  paymentIntroModal,
  pricingPage,
} = eventLocations;

export default {
  name: 'Subscription',

  modalName: paymentModalNames.paymentIntroModal, // to send with Segment events
  Attributes,
  EventHelper,
  ModalEventBus,

  components: {
    Button,
    ButtonLink,
    CustomIcon,
    HeadingExtraSmall,
    HeadingLarge,
    HighlightTagIcon,
    Icon,
    Tooltip,
  },

  mixins: [PaymentStepMixin, SubscriptionFeaturesMixin],

  props: {
    currentPlan: {
      required: true,
      type: Object,
    },
    isAnnual: {
      required: true,
      type: Boolean,
    },
    isPricingPage: {
      default: false,
      type: Boolean,
    },
    item: {
      required: true,
      type: Object,
    },
  },

  computed: {
    ...mapGetters(checkoutModel, [
      'cloudDirectoryAndSupport',
    ]),

    ...mapGetters(planModule, [
      'getRecommendedPlan',
    ]),

    ...mapGetters(subscriptionCollection, {
      subscriptionPrice: 'getSubscriptionPrice',
    }),

    ...mapGetters('AccountManagerCalendarModule', [
      'getAccountManagerCalendarUrl',
    ]),

    billingType() {
      return this.isAnnual ? 'Annual Billing' : 'Monthly Billing';
    },

    button() {
      const isALaCartePaywall = this.isALaCarte && !this.isPricingPage;

      if (isALaCartePaywall) {
        return {
          action: this.handleALaCartePaywallClick,
          dataTestId: 'Subscription__goToPricingPage',
          text: 'go to pricing page',
        };
      }

      if (this.isALaCarte && this.isPricingPage) {
        return {
          action: this.handleSelectFeaturesClick,
          dataTestId: 'Subscription__selectFeaturesButton',
          text: 'select features',
        };
      }

      const buyNowButton = {
        action: this.openPaymentFormFromPaywall,
        dataTestId: 'Subscription__paywallBuyNowButton',
        text: 'buy now',
      };

      // This is needed because when it's triggered from the paywall,
      // the flow is handled with steps, defining the content of each step (Intro -> Form -> Outro)
      // when it's triggered from the pricing page, we only need to open the payment form in a modal
      if (this.isPricingPage && !this.isALaCarte) {
        buyNowButton.action = this.openPaymentForm;
        buyNowButton.dataTestId = 'Subscription__pricingBuyNowButton';
      }

      return buyNowButton;
    },

    details() {
      const type = ProductCodeToSubscriptionType[this.item.productCode];
      return SubscriptionDetails[type] || { specification: {} };
    },

    hasBestValue() {
      return (this.item.productCode !== this.currentPlan.productCode)
      && !!this.details.bestValue;
    },

    hasPackageRecommendation() {
      return this.hasBestValue;
    },

    hasUsageRecommendation() {
      return false;
    },

    isALaCarte() {
      return this.item.productCode === getProductCodeByType(SubscriptionTypes.alaCarte);
    },
  },

  methods: {
    ...mapActions(checkoutModel, [
      'toggleAddOn',
    ]),

    featuresWithAssociatedPlan(features) {
      return features.filter(feature => !feature.optionalAddon);
    },

    handleALaCartePaywallClick() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'A La Carte selected from paywall',
        [text]: 'go to pricing page',
        [locationEventProp]: paymentIntroModal,
        [section]: 'Paywall',
      });
      this.$options.ModalEventBus.close();
      Util.redirect(ItemTypeRoutes[ItemTypeConsts.pricing]);
    },

    handleDetailsClick(subscription) {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'jump to feature matrix',
        [text]: 'details',
        [locationEventProp]: pricingPage,
        [section]: `Package Descriptions: ${subscription.displayName}: ${this.billingType}`,
      });
      this.$emit('viewFeatureMatrix');
    },

    handleSelectFeaturesClick() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'jump to feature matrix',
        [text]: 'select features',
        [locationEventProp]: pricingPage,
        [section]: 'A La Carte subscription',
      });

      if (this.subscription.addOns.length === 0) {
        this.updateItem(this.item);
        this.toggleAddOn(this.cloudDirectoryAndSupport);
      }

      this.$emit('viewFeatureMatrix');
    },

    async openPaymentForm(modal, subscription) {
      // in the event the admin adds things to cart via A La Carte
      // remove those features/add-ons
      this.resetSubscription();
      await this.updateItem(subscription);
      this.triggerBuyNowEvent(pricingPage, subscription.displayName);
      PaymentHelper.openCheckoutModal(pricingPage);
    },

    async openPaymentFormFromPaywall(modal, subscription) {
      this.triggerBuyNowEvent(modal, subscription.displayName);
      await this.handleBuyNowButtonClicked(modal, subscription);
    },

    price(productCode) {
      let price = this.subscriptionPrice(productCode);
      if (!price) {
        const { annualPrice, listPrice } = this.item;
        price = this.isAnnual ? fixDecimals(annualPrice) : fixDecimals(listPrice);
      }
      return price;
    },

    triggerBuyNowEvent(location, subscriptionName) {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'buy now button clicked',
        [text]: 'buy now',
        [locationEventProp]: location,
        [section]: `Package Descriptions: ${subscriptionName}: ${this.billingType}`,
      });
    },
  },
};
</script>
<style scoped module>
.attribute {
  font-size: 0.9rem;
  margin-left: 1rem;
  max-width: 14rem;
}

.attributeWithDescription {
  text-decoration: underline;
}

.background {
  max-width: 300px;
}

.body {
  padding: 10px;
}

.border {
  border-bottom: var(--jc-border);
}

.description {
  color: var(--jc-text-color-light);
  font-size: 0.9rem;
  font-weight: normal;
  line-height: 1rem;
}

.detailsButton.detailsButton {
  margin: 1rem;
  padding-top: 1rem;
}

.fade,
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.features {
  margin-left: 15px;
  margin-top: 15px;
  text-align: left;
}

.featureIcon {
  color: var(--jcPrimary);
  top: 3px;
}

.featureWrapper {
  display: flex;
  margin-top: var(--jc-spacer-small);
}

.groupName {
  font-size: 0.9rem;
  font-weight: 600;
  margin-top: var(--jc-spacer);
}

.headerBackground {
  border-radius: 2px;

  /* jcTertiary w/ 12% opacity */
  box-shadow: 0 -16px 24px 0 rgba(240, 252, 253, 0.12);
}

.headerCurrentPlan {
  color: var(--jcGrey);
  text-transform: uppercase;
}

.headerEmpty {
  min-height: 40px;
}

.headerPlanBlock {
  font-size: 0.9rem;
  font-weight: bold;
  padding: 1rem;
}

.headerRecommend {
  background-color: var(--jc-color-informational-base);
  color: white;
}

.highlightTagIcon {
  float: left;
}

.price {
  padding: 10px;
}

.priceAmount {
  font-size: 3.5rem;
  font-weight: bold;
  position: relative;
}

.priceDescription {
  color: var(--jc-text-color-light);
}

.priceDollar {
  left: -10px;
  position: absolute;
  top: 10px;
}

.priceText {
  font-size: 0.8rem;
  font-weight: normal;
}
</style>
